import { useEffect } from 'react';
import type { TodayProposalView } from '@heraion/shared';

import type { TFn } from '../i18nTypes';
import { InlineStatusHint } from '../primitives/InlineStatusHint';
import { useLiveAnnouncement } from '../primitives/useLiveAnnouncement';
import type { Theme } from './theme';

// #360: what the «today» draft says when its history came from this device.

/** The line under the draft's header, or null when the loads are the server's. */
export function todayLoadText(t: TFn, view: TodayProposalView): string | null {
  if (view.loadSource === 'cache') return t('mobile.logger.draftToday.loadsCached');
  if (view.loadSource === 'partial') return t('mobile.logger.draftToday.loadsPartial');
  return null;
}

/** A row with no anchor: never done, or unknown to this device. */
export function todayNoAnchorText(t: TFn, view: TodayProposalView, slug: string): string {
  if (view.unknownSlugs?.includes(slug)) return t('mobile.logger.draftToday.notOnDevice');
  return t('mobile.logger.draftToday.noHistory');
}

// The builds already announced. Keyed by the build's candidates, which every
// refit of the draft carries over by reference (useTodayDraft): a card that
// mounts again over the same draft stays quiet, a new build speaks.
const announced = new WeakSet<object>();

export function TodayLoadHint({
  t,
  proposal,
  theme,
}: {
  t: TFn;
  proposal: TodayProposalView;
  theme: Theme;
}) {
  const text = todayLoadText(t, proposal);
  const { speak, region } = useLiveAnnouncement();
  // Spoken once, when the draft first holds a load source (EditorSaveStatus
  // speaks only on a change, #336); the row itself is not a live region.
  const build = proposal.candidates ?? null;
  useEffect(() => {
    if (text === null || build === null || announced.has(build)) return;
    announced.add(build);
    speak(text);
  }, [text, build, speak]);
  if (text === null) return null;
  return (
    <>
      <InlineStatusHint theme={theme} tone="cached" text={text} live={false} />
      {region}
    </>
  );
}
